import React from 'react';
import { InfoModal } from '../../../../common/InfoModal';

const CORE_REQUIRED = [
    { doc: 'NSPIRE / REAC Reports', purpose: 'Physical and life safety condition', date: 'Most recent 24 months' },
    { doc: 'CNA / PCNA Reports', purpose: 'Capital and reserve risk', date: 'Most recent 3–5 years' },
    { doc: 'Audited T-12 Financials', purpose: 'Financial and NOI performance', date: 'Most recent 12 months' },
    { doc: 'Certified Rent Rolls', purpose: 'Occupancy and rent validation', date: 'Most recent 3 months' },
    { doc: 'IRS Form 8823 / MOR Reports', purpose: 'Compliance exposure', date: 'Most recent 3 years' },
    { doc: 'LURA / Regulatory Agreements', purpose: 'Affordability restrictions', date: 'Current agreement' },
];

const CONFIDENCE_ENHANCERS = [
    { doc: 'Work Order Logs', purpose: 'Maintenance and operational trends', date: 'Most recent 12 months' },
    { doc: 'Reserve Studies', purpose: 'Reserve adequacy', date: 'Most recent 3–5 years' },
    { doc: 'Staffing & Training Records', purpose: 'Operational competency (ROTI)', date: 'Most recent 12 months' },
    { doc: 'Third Party QA Reports', purpose: 'Independent validation', date: 'Most recent 24 months' },
];

const DO_NOT_UPLOAD = [
    { doc: 'Sensitive Personal Info', purpose: 'Privacy and security protection' },
    { doc: 'Financial Account Data', purpose: 'Financial security protection' },
    { doc: 'Medical / Biometric Info', purpose: 'Protected personal information' },
    { doc: 'Legal Privileged Docs', purpose: 'Protected legal communications' },
];

function GuidanceTable({ rows, showDate = true }) {
    return (
        <table className="w-full text-sm">
            <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-2 pr-3 font-medium">Document</th>
                    <th className="py-2 pr-3 font-medium">Purpose</th>
                    {showDate && <th className="py-2 font-medium">Preferred Date</th>}
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.doc} className="border-b border-slate-100 dark:border-slate-800 last:border-b-0">
                        <td className="py-2 pr-3 font-medium text-slate-800 dark:text-slate-100">{row.doc}</td>
                        <td className="py-2 pr-3 text-slate-600 dark:text-slate-300">{row.purpose}</td>
                        {showDate && <td className="py-2 text-slate-600 dark:text-slate-300">{row.date}</td>}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default function DocumentGuidanceModal({ isOpen, onClose }) {
    return (
        <InfoModal isOpen={isOpen} onClose={onClose} title="JPRO Index Master Document Framework">
            <div className="space-y-6">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                    Stronger inputs unlock higher confidence scores. Use this framework to decide what to upload — and what to keep out.
                </p>

                <section>
                    <h4 className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-2">
                        Core Required <span className="text-slate-500 font-normal normal-case">— foundational, expected for every property</span>
                    </h4>
                    <GuidanceTable rows={CORE_REQUIRED} />
                </section>

                <section>
                    <h4 className="text-sm font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider mb-2">
                        Confidence Enhancers <span className="text-slate-500 font-normal normal-case">— optional, raise the score ceiling</span>
                    </h4>
                    <GuidanceTable rows={CONFIDENCE_ENHANCERS} />
                </section>

                <section>
                    <h4 className="text-sm font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider mb-2">
                        Do Not Upload <span className="text-slate-500 font-normal normal-case">— never accepted</span>
                    </h4>
                    <div className="rounded-md border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/10 p-3">
                        <GuidanceTable rows={DO_NOT_UPLOAD} showDate={false} />
                        <p className="mt-3 text-xs text-red-700 dark:text-red-300">
                            JPRO will reject these document types. If a required document contains any of this information, redact before uploading.
                        </p>
                    </div>
                </section>
            </div>
        </InfoModal>
    );
}
